import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  type NavigateOptions,
  type ToOptions,
  RouterProvider,
  createRouter,
} from '@tanstack/react-router'
import React from 'react'

import { AuthProvider, useAuth } from '@/auth'
import './i18n'

import { RouterProvider as ReactAriaRouterProvider } from 'react-aria-components'

// Create a client
const queryClient = new QueryClient()

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: undefined!,
  },
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module 'react-aria-components' {
  interface RouterConfig {
    href: ToOptions['to']
    routerOptions: Omit<NavigateOptions, keyof ToOptions>
  }
}

export default function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  )
}

function InnerApp() {
  const auth = useAuth()

  return (
    <QueryClientProvider client={queryClient}>
      <ReactAriaRouterProvider
        navigate={(to, options) => router.navigate({ to, ...options })}
        // There is bug in react-aria-components or on Tanstack Router that prevents href from working. In any case, the useHref hook is optional
        // useHref={(to) => router.buildLocation(to).href}
      >
        <RouterProvider router={router} basepath="/nutritionist" context={{ auth }} />
      </ReactAriaRouterProvider>
    </QueryClientProvider>
  )
}
